import { useMemo, useState } from 'react';

import { Box, Skeleton } from '@mui/material';
import { ApexOptions } from 'apexcharts';
import { groupBy } from 'lodash';
import ReactApexChart from 'react-apexcharts';
import { useQuery } from 'react-query';
import { If } from 'src/components/If';
import Subtitle from 'src/components/Subtitle';
import { getSubordinationDataCharts } from 'src/services/operation/operationFidc';

import { GroupButtonPeriod, Period } from '../../components/GroupButtonPeriod';
import {
  colors,
  fakeSubordinationData,
  getLimitZoomMountedHandler,
  getPeriodDate,
  getQuotaKey,
  locale,
  noData,
} from '../utils';

const defaultPeriodInDays = 180;

type SubordinationChartsProps = {
  hashId: string;
  hasPermission: boolean;
};

export function SubordinationCharts({
  hashId,
  hasPermission,
}: SubordinationChartsProps) {
  const [selectedQuotas, setSelectedQuotas] = useState<string[]>([]);
  const [selectedPeriod, setSelectedPeriod] =
    useState<Period>(defaultPeriodInDays);

  const { data: rawData, isLoading } = useQuery({
    queryKey: ['subordination-charts', hashId, selectedPeriod],
    queryFn: () =>
      getSubordinationDataCharts(hashId, getPeriodDate(selectedPeriod)),
    onSuccess: (data) => {
      const multiclasseData = data.filter((item) => item.ismulticlasse);
      const groupedByQuotaSerie = groupBy(multiclasseData, getQuotaKey);
      setSelectedQuotas(Object.keys(groupedByQuotaSerie));
    },
    enabled: !!hashId,
    refetchOnWindowFocus: false,
  });

  const handlePeriodChange = (days: Period) => {
    setSelectedPeriod(days);
  };

  const data = hasPermission ? rawData : fakeSubordinationData;

  const hasMulticlasseData = useMemo(() => {
    return data?.some((item) => item.ismulticlasse) ?? false;
  }, [data]);

  const chartData = useMemo(() => {
    if (!data || data.length === 0 || selectedQuotas.length === 0) {
      return { series: [] };
    }

    if (!isLoading && !hasMulticlasseData) {
      return { series: [] };
    }

    const filteredData = data.filter(
      (item) =>
        item.ismulticlasse && selectedQuotas.includes(getQuotaKey(item)),
    );

    if (filteredData.length === 0) {
      return { series: [] };
    }

    const allDates = [...new Set(filteredData.map((item) => item.data))].sort();

    const seniorByDate = filteredData.reduce(
      (acc, item) => {
        if (!acc[item.data]) {
          acc[item.data] = [];
        }
        acc[item.data].push(item.subordinacaoSenior);
        return acc;
      },
      {} as Record<string, number[]>,
    );

    const mezaninoByDate = filteredData.reduce(
      (acc, item) => {
        if (!acc[item.data]) {
          acc[item.data] = [];
        }
        acc[item.data].push(item.subordinacaoMezanino);
        return acc;
      },
      {} as Record<string, number[]>,
    );

    const series = [
      {
        name: 'Subordinação Sênior',
        data: allDates.map((date) => {
          const timestamp = new Date(date).getTime();
          const values = seniorByDate[date] || [];
          const value =
            values.length > 0
              ? values.reduce((sum, val) => sum + val, 0) / values.length
              : null;
          return {
            x: timestamp,
            y: value,
          };
        }),
      },
      {
        name: 'Subordinação Mezanino',
        data: allDates.map((date) => {
          const timestamp = new Date(date).getTime();
          const values = mezaninoByDate[date] || [];
          const value =
            values.length > 0
              ? values.reduce((sum, val) => sum + val, 0) / values.length
              : null;
          return {
            x: timestamp,
            y: value,
          };
        }),
      },
    ];

    return { series };
  }, [data, selectedQuotas]);

  const options: ApexOptions = {
    series: chartData.series,
    colors: colors,
    chart: {
      type: 'line',
      defaultLocale: 'pt-BR',
      locales: [locale],
      zoom: {
        type: 'xy',
        enabled: true,
        autoScaleYaxis: false,
      },
      toolbar: {
        autoSelected: 'zoom',
      },
      events: {
        updated: getLimitZoomMountedHandler(),
      },
    },
    stroke: {
      curve: 'smooth',
      width: 4,
    },
    dataLabels: {
      enabled: false,
    },
    markers: {
      size: 0,
      hover: {
        size: 6,
      },
    },
    legend: {
      show: true,
      showForSingleSeries: true,
      position: 'top',
      horizontalAlign: 'center',
      floating: false,
      offsetY: 20,
      height: 50,
      itemMargin: {
        horizontal: 10,
        vertical: 5,
      },
    },
    yaxis: {
      labels: {
        formatter: function (val) {
          return val.toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
        },
      },
      title: {
        text: 'Subordinação (%)',
        style: {
          fontFamily: 'Inter',
        },
      },
    },
    xaxis: {
      type: 'datetime',
    },
    tooltip: {
      shared: true,
      intersect: false,
      x: {
        format: 'dd/MM/yyyy',
      },
      y: {
        formatter: function (val) {
          if (val === null || val === undefined) return '';
          return (
            val.toLocaleString('pt-BR', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }) + '%'
          );
        },
      },
    },
    noData: noData,
  };

  return (
    <Box sx={{ '.apexcharts-menu-item.exportCSV': { display: 'none' } }}>
      <Subtitle title="Subordinação" />
      <If condition={isLoading}>
        <Skeleton variant="rectangular" animation="wave" height={420} />
      </If>
      <If condition={!isLoading}>
        <Box
          width="100%"
          display="flex"
          flexDirection="column"
          gap={2}
          alignItems="flex-end">
          <GroupButtonPeriod
            selectedPeriod={selectedPeriod}
            handlePeriodChange={handlePeriodChange}
          />
        </Box>
        <ReactApexChart
          type="line"
          series={chartData.series}
          options={options}
          height={420}
        />
      </If>
    </Box>
  );
}
